import type Phaser from 'phaser';
import { t, setLocale, getLocale, LOCALES, type Locale } from '@i18n/index';
import {
  PALETTE,
  TEXT_COLOUR,
  FONT,
  titleStyle,
  readoutStyle,
  labelStyle,
  px,
  getTextScale,
  setTextScale,
  TEXT_SCALES,
  type TextScale,
} from '../theme';
import { audio } from '../audio/engine';
import { loadSettings, saveSettings } from '../saveGame';
import { analyticsBlockedBecause, analyticsOptedOut, setAnalyticsOptOut } from '../analytics';

/**
 * Einstellungen, von ueberall erreichbar.
 *
 * Bis M7 liessen sich Sprache und Ton nur im Hauptmenue umstellen — wer
 * mitten in einer Kampagne merkte, dass er lieber englisch liest, musste den
 * Durchlauf verlassen. Und die Schriftgroesse gab es gar nicht, obwohl das
 * Spiel fast nur aus Text besteht und dessen Grundgroesse bei elf Pixeln
 * liegt.
 *
 * Beides aendert die Darstellung von Grund auf, deshalb baut die aufrufende
 * Szene sich danach neu auf. Das ist der Grund fuer `onApply`: Das Fenster
 * weiss nicht, wie die Szene darunter aussieht, nur dass sie es noch einmal
 * tun muss.
 */

export interface SettingsOptions {
  /** Laeuft, wenn sich etwas geaendert hat und die Szene neu zeichnen muss. */
  readonly onApply: () => void;
}

export function showSettings(scene: Phaser.Scene, options: SettingsOptions): void {
  const { width, height } = scene.scale;

  // Alle Masse haengen an der Schriftgroesse. Mit festen Zahlen lief der
  // Dialog bei 150 % unten aus dem Rahmen — ausgerechnet die Einstellung, mit
  // der man das erst hinbekommt.
  const scale = getTextScale();
  const panelWidth = Math.min(Math.round(380 * scale), width - 48);

  // Der Hinweisabsatz wird vorab gemessen, damit die Hoehe stimmt statt zu
  // schaetzen — bei drei Zeilen statt einer sind das siebzig Pixel Unterschied.
  const hint = scene.add.text(0, 0, t('settings.hint'), {
    ...readoutStyle(10, true),
    wordWrap: { width: panelWidth - 48 },
    align: 'center',
  });

  const rows = 4;
  const footer = Math.round(34 * scale);

  /*
   * Zeilenabstand nachgeben lassen, bevor der Dialog aus dem Fenster laeuft.
   *
   * Mit der vierten Zeile (Reichweitenmessung) passte er bei doppelter Schrift
   * auf einem flachen Fenster nicht mehr; `top` wurde negativ und der Titel
   * verschwand nach oben — ausgerechnet bei der Einstellung, mit der man
   * grosse Schrift erst bekommt.
   *
   * Nachgegeben wird beim *Abstand*, nicht bei der Schrift: Die Zeilen ruecken
   * zusammen, bleiben aber gleich gross. Unterhalb der Mindestwerte wird nicht
   * mehr gedrueckt — dann ist das Fenster schlicht zu flach, und ein Dialog,
   * dessen Zeilen ineinanderlaufen, waere schlechter als einer, der oben
   * anstoesst.
   */
  const available = height - 16;
  const roomy = { row: Math.round(46 * scale), header: Math.round(74 * scale) };
  const tight = { row: Math.round(30 * scale), header: Math.round(52 * scale) };

  const full = roomy.header + rows * roomy.row + hint.height + footer;
  const slack = rows * (roomy.row - tight.row) + (roomy.header - tight.header);
  const squeeze = full > available && slack > 0 ? Math.min(1, (full - available) / slack) : 0;

  const rowHeight = Math.round(roomy.row - (roomy.row - tight.row) * squeeze);
  const headerHeight = Math.round(roomy.header - (roomy.header - tight.header) * squeeze);

  const panelHeight = headerHeight + rows * rowHeight + hint.height + footer;
  const left = (width - panelWidth) / 2;
  const top = Math.max(8, (height - panelHeight) / 2);

  // Alles darunter abdunkeln und Klicks abfangen: Anders als Roskopfs
  // Einblendung ist das hier ein Dialog — wer die Schriftgroesse aendert,
  // soll nicht gleichzeitig versehentlich auslaufen.
  const shade = scene.add
    .rectangle(0, 0, width, height, PALETTE.abyss, 0.86)
    .setOrigin(0, 0)
    .setDepth(2000)
    .setInteractive();

  const frame = scene.add.rectangle(0, 0, panelWidth, panelHeight, PALETTE.panel, 0.98).setOrigin(0, 0);
  frame.setStrokeStyle(1, PALETTE.panelEdge);

  const container = scene.add.container(left, top, [frame]);
  container.setDepth(2001);

  container.add(
    scene.add
      .text(panelWidth / 2, Math.round(26 * scale), t('settings.title'), titleStyle(18))
      .setOrigin(0.5),
  );

  let changed = false;
  const rebuild = (): void => {
    changed = true;
    persist();
    shade.destroy();
    container.destroy();
    options.onApply();
  };

  const pad = Math.round(20 * scale);
  const gap = Math.round(54 * scale);
  let y = headerHeight;

  // Sprache
  container.add(scene.add.text(pad, y, t('settings.language'), labelStyle()));
  LOCALES.forEach((locale, index) => {
    container.add(
      choice(
        scene,
        panelWidth - pad - (LOCALES.length - 1 - index) * gap,
        y,
        locale.toUpperCase(),
        getLocale() === locale,
        () => {
          if (getLocale() === locale) return;
          setLocale(locale);
          rebuild();
        },
      ),
    );
  });
  y += rowHeight;

  // Schriftgroesse
  container.add(scene.add.text(pad, y, t('settings.textsize'), labelStyle()));
  TEXT_SCALES.forEach((step, index) => {
    container.add(
      choice(
        scene,
        panelWidth - pad - (TEXT_SCALES.length - 1 - index) * Math.round(44 * scale),
        y,
        `${Math.round(step * 100)}`,
        getTextScale() === step,
        () => {
          if (getTextScale() === step) return;
          setTextScale(step);
          rebuild();
        },
      ),
    );
  });
  y += rowHeight;

  // Ton
  container.add(scene.add.text(pad, y, t('settings.sound'), labelStyle()));
  container.add(
    choice(
      scene,
      panelWidth - pad,
      y,
      t(audio.isMuted ? 'menu.sound.off' : 'menu.sound.on'),
      !audio.isMuted,
      () => {
        audio.setMuted(!audio.isMuted);
        rebuild();
      },
    ),
  );
  y += rowHeight;

  /*
   * Reichweitenmessung.
   *
   * Gehoert hierher und nicht in eine Fussnote im Impressum: Ein Widerspruch,
   * den man erst suchen muss, ist keiner. Der Schalter nennt auch den Grund,
   * wenn ohnehin nicht gemessen wird — bei gesetztem „Do Not Track" waere ein
   * scheinbar aktiver Schalter schlicht gelogen.
   */
  const blocked = analyticsBlockedBecause();
  const forced = blocked === 'dnt' || blocked === 'unconfigured';

  container.add(scene.add.text(pad, y, t('settings.analytics'), labelStyle()));
  container.add(
    choice(
      scene,
      panelWidth - pad,
      y,
      forced
        ? t(blocked === 'dnt' ? 'settings.analytics.dnt' : 'settings.analytics.off')
        : t(blocked === 'optout' ? 'settings.analytics.off' : 'settings.analytics.on'),
      !forced && blocked === null,
      () => {
        if (forced) return;
        setAnalyticsOptOut(!analyticsOptedOut());
        rebuild();
      },
    ),
  );
  y += rowHeight;

  hint.setPosition(panelWidth / 2, y).setOrigin(0.5, 0);
  container.add(hint);

  const close = scene.add
    .text(panelWidth - pad, Math.round(24 * scale), t('settings.close'), labelStyle(12, PALETTE.inkDim))
    .setOrigin(1, 0.5)
    .setInteractive({ useHandCursor: true });
  close.on('pointerover', () => close.setColor(TEXT_COLOUR.coast));
  close.on('pointerout', () => close.setColor(PALETTE.inkDim));
  close.on('pointerdown', () => {
    audio.resume();
    persist();
    shade.destroy();
    container.destroy();
    // Auch ohne Aenderung neu zeichnen waere unnoetige Arbeit — und wuerde
    // eine halb getippte Eingabe im Namensfeld verlieren.
    if (changed) options.onApply();
  });
  container.add(close);

  function persist(): void {
    saveSettings({ locale: getLocale(), muted: audio.isMuted, textScale: getTextScale() });
  }
}

/** Eine kleine Umschaltmarke. Gewaehltes hell, Uebriges gedaempft. */
function choice(
  scene: Phaser.Scene,
  right: number,
  y: number,
  label: string,
  selected: boolean,
  onClick: () => void,
): Phaser.GameObjects.Text {
  const text = scene.add
    .text(right, y, label, {
      fontFamily: FONT.mono,
      fontSize: px(12),
      color: selected ? TEXT_COLOUR.coast : PALETTE.inkFaint,
    })
    .setOrigin(1, 0)
    .setInteractive({ useHandCursor: true });

  text.on('pointerover', () => {
    if (!selected) text.setColor(PALETTE.ink);
  });
  text.on('pointerout', () => {
    if (!selected) text.setColor(PALETTE.inkFaint);
  });
  text.on('pointerdown', () => {
    audio.resume();
    audio.cue('select');
    onClick();
  });

  return text;
}

/**
 * Die Schaltflaeche, die den Dialog oeffnet.
 *
 * Sitzt in jeder Szene an derselben Stelle oben rechts, damit man sie nicht
 * suchen muss.
 */
export function createSettingsButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  onApply: () => void,
): Phaser.GameObjects.Text {
  const label = scene.add
    .text(x, y, t('settings.open'), labelStyle(11))
    .setOrigin(1, 0.5)
    .setInteractive({ useHandCursor: true });

  label.on('pointerover', () => label.setColor(TEXT_COLOUR.coast));
  label.on('pointerout', () => label.setColor(PALETTE.inkFaint));
  label.on('pointerdown', () => {
    audio.resume();
    showSettings(scene, { onApply });
  });

  return label;
}

/** Stellt die gesicherten Einstellungen beim Start wieder her. */
export function applyStoredSettings(): void {
  const settings = loadSettings();
  if (settings === null) return;

  setLocale(settings.locale);
  audio.setMuted(settings.muted);
  // Ein gesicherter Wert kann alles Moegliche sein — etwa aus einer Fassung
  // mit anderen Stufen. Nur was in der aktuellen Liste steht, wird uebernommen.
  const stored = TEXT_SCALES.find((scale) => scale === settings.textScale);
  if (stored !== undefined) setTextScale(stored);
}

export type { Locale, TextScale };
